import { createRealtimeSession, refreshVoiceInstructions } from '../utils/agentApiUtils';
import {
  buildVerbatimEnglishGreetingInstructions,
  buildVoiceGreetingInstructions,
  containsNonEnglishSpokenScript,
} from './voiceLanguagePolicy';
import { isMeaningfulUserSpeech } from './voiceTurnValidation';
import VoiceMicActivity from './voiceMicActivity';
import VoiceFeatureSegments, {
  ANALYSER_FFT_SIZE,
  GATE_SAMPLES,
  createBandBins,
  createFrameScratch,
  readFrameFeatures,
  readRms,
} from './voiceAudioFeatures';
import { closeAnalysisStream, openAnalysisStream } from './voiceAnalysisStream';
import { exposeTelemetry, OUTCOMES, voiceFeatureTelemetry } from './voiceFeatureTelemetry';

// GA Realtime API: browser POSTs its SDP offer to /realtime/calls using the
// ephemeral token. The model is bound to the token server-side, so no query.
const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

/** Wait for session.created before greeting; fallback if that event never arrives. */
const GREETING_FALLBACK_MS = 3000;
/** Recover to listening if a greeting response never produces audio. */
const GREETING_RESPONSE_TIMEOUT_MS = 20000;
/** Ignore VAD / new turns briefly after Spacey speaks (echo guard). */
const ASSISTANT_ECHO_COOLDOWN_MS = 1800;
/** Min chars for a normal assistant bubble (skip obvious cut-off fragments). */
const MIN_ASSISTANT_CAPTION_CHARS = 12;
/** Wait for async user transcription after VAD speech_stopped before giving up. */
const TRANSCRIPT_WAIT_MS = 6000;
/** Re-check window for speech that starts too quietly to clear the noise floor at once. */
const BARGE_IN_RECHECK_MS = 400;

/**
 * Ask the browser for its own echo cancellation, steady-noise suppression and
 * level normalisation instead of relying on per-platform defaults.
 */
const MIC_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const responseHasAudioOutput = (response) => {
  if (!Array.isArray(response?.output)) return false;
  return response.output.some((item) => {
    if (item?.type !== 'message' || !Array.isArray(item.content)) return false;
    return item.content.some((part) => String(part?.type || '').includes('audio'));
  });
};

/**
 * RealtimeVoiceClient — Phase 0
 *
 * Manages a live, hands-free voice conversation with OpenAI's Realtime API
 * over WebRTC. Audio flows directly browser <-> OpenAI (lowest latency); our
 * backend only mints the short-lived ephemeral session token.
 *
 * Phase 0 scope: plain spoken conversation (no tool calling yet). Tool calls
 * (search_available_spaces, etc.) are wired in Phase 1.
 *
 * Lifecycle callbacks (all optional):
 *   onStatus(status)  - 'connecting' | 'live' | 'closed' | 'error'
 *   onPhase(phase)    - 'listening' | 'thinking' | 'searching' | 'speaking'
 *   onError(error)
 *   onEvent(event)    - raw realtime data-channel events (debug / future use)
 *   executeToolCall(name, args) => Promise<result> - runs a tool call on the
 *       backend; `result.modelOutput` is fed back to the model, the rest is UI.
 *   onToolResult(result, {name, args}) - lets the UI render cards/actions.
 *   onCaption({ role, text, final }) - live transcript for user/assistant.
 *   onLevel(level) - 0..1 audio amplitude for the reactive orb.
 */
export default class RealtimeVoiceClient {
  constructor({
    onStatus, onPhase, onError, onEvent, executeToolCall, onToolResult, onCaption, onLevel,
  } = {}) {
    this.onStatus = onStatus || (() => {});
    this.onPhase = onPhase || (() => {});
    this.onError = onError || (() => {});
    this.onEvent = onEvent || (() => {});
    this.executeToolCall = executeToolCall || null;
    this.onToolResult = onToolResult || (() => {});
    this.onCaption = onCaption || (() => {});
    this.onLevel = onLevel || (() => {});

    this.pc = null;
    this.dc = null;
    this.micStream = null;
    this.audioEl = null;
    this.closed = false;
    this.handledCallIds = new Set();
    this.assistantTranscript = '';
    this.phase = 'listening';
    this.userMuted = false;
    this.userContext = {};
    this.instructionsRefreshPromise = null;
    this.greetingSent = false;
    this.greetingRetryUsed = false;
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = false;
    this.greetingFallbackTimer = null;
    this.greetingResponseTimer = null;
    this.activeResponseId = null;
    this.greetingResponseId = null;
    /** Deferred connect-greeting caption — emitted once, after playback finishes. */
    this.pendingGreetingCaption = null;

    this.greetingPlaybackFallbackTimer = null;
    this.audioCtx = null;
    this.micAnalyser = null;
    this.remoteAnalyser = null;
    this.micScratch = null;
    this.micBands = null;
    this.remoteScratch = null;
    this.levelRaf = null;
    /** Optional unprocessed capture used for measurement only (see voiceAnalysisStream). */
    this.analysisStream = null;

    /** responseId → 'in_progress' | 'completed' | 'cancelled' | 'failed' */
    this.responseStatuses = new Map();
    this.committedResponseIds = new Set();
    /** Deferred assistant caption until response completes + playback stops. */
    this.pendingAssistantCommit = null;
    this.assistantEchoCooldownUntil = 0;
    /** Set before client-initiated response.create; cleared on response.created. */
    this.expectingClientResponse = false;
    /** Skip post-playback echo cooldown when the user intentionally barged in. */
    this.userBargeInActive = false;
    this.echoCooldownTimer = null;
    /** Tracks an in-progress user VAD turn until transcript is validated. */
    this.activeUserTurn = null;
    this.transcriptWaitTimer = null;
    /** Adaptive noise-floor tracker used to tell the user apart from ambient noise. */
    this.micActivity = new VoiceMicActivity();
    this.bargeInRecheckTimer = null;
    /** Measurement only — collects proximity features per VAD burst, decides nothing. */
    this.featureSegments = new VoiceFeatureSegments();
    this.telemetry = voiceFeatureTelemetry;
    this.sessionKey = null;
  }

  static isSupported() {
    return (
      typeof window !== 'undefined'
      && typeof window.RTCPeerConnection === 'function'
      && !!navigator?.mediaDevices?.getUserMedia
    );
  }

  async start(userContext = {}, voice) {
    if (!RealtimeVoiceClient.isSupported()) {
      const err = new Error('Live voice mode is not supported in this browser.');
      this.onError(err);
      this.onStatus('error');
      throw err;
    }

    this.userContext = userContext || {};
    this.greetingSent = false;
    this.greetingRetryUsed = false;
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = true;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.responseStatuses = new Map();
    this.committedResponseIds = new Set();
    this.pendingAssistantCommit = null;
    this.assistantEchoCooldownUntil = 0;
    this.expectingClientResponse = false;
    this.userBargeInActive = false;
    this.activeUserTurn = null;
    this.clearTranscriptValidationTimeout();
    this.clearBargeInRecheck();
    this.micActivity.reset();
    this.featureSegments.reset();
    this.sessionKey = `s${Date.now().toString(36)}`;
    exposeTelemetry(this.telemetry);
    if (this.echoCooldownTimer) {
      clearTimeout(this.echoCooldownTimer);
      this.echoCooldownTimer = null;
    }

    try {
      this.onStatus('connecting');
      this.setPhase('speaking');

      // 1. Mint an ephemeral session from our backend.
      // GA response: top-level `value` (ek_...). Older shape: client_secret.value.
      const session = await createRealtimeSession(userContext, voice);
      if (this.closed) return;
      const ephemeralKey = session?.value || session?.client_secret?.value;
      if (!ephemeralKey) {
        throw new Error('No ephemeral key returned from realtime-session.');
      }

      // 2. Peer connection + remote audio sink.
      const pc = new RTCPeerConnection();
      this.pc = pc;

      this.audioEl = document.createElement('audio');
      this.audioEl.autoplay = true;
      this.audioEl.setAttribute('playsinline', 'true');
      this.audioEl.playsInline = true;
      pc.ontrack = (event) => {
        if (this.audioEl && event.streams && event.streams[0]) {
          this.audioEl.srcObject = event.streams[0];
          this.attachRemoteAnalyser(event.streams[0]);
        }
      };

      pc.onconnectionstatechange = () => {
        if (this.closed) return;
        const state = pc.connectionState;
        if (state === 'connected') {
          this.onStatus('live');
          // Stay in speaking until the connect greeting finishes — not listening.
          if (this.awaitingConnectGreeting) {
            this.setPhase('greeting');
          } else {
            this.setPhase('listening');
          }
        } else if (state === 'failed' || state === 'disconnected') {
          this.onStatus('error');
        }
      };

      // 3. Microphone.
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS });
      if (this.closed) { this.releaseResources(); return; }
      await this.requestVoiceIsolation();
      this.micStream.getTracks().forEach((track) => pc.addTrack(track, this.micStream));
      // Measurement runs on an unprocessed copy of the mic when one is available,
      // so the uplink keeps the DSP that already fixed echo and steady noise.
      this.analysisStream = await openAnalysisStream(this.micStream);
      if (this.closed) { this.releaseResources(); return; }
      this.attachMicAnalyser(this.analysisStream.stream || this.micStream);
      this.startLevelMetering();
      try {
        const ctx = this.ensureAudioContext();
        if (ctx?.state === 'suspended') await ctx.resume();
      } catch { /* best-effort for iOS */ }

      // 4. Data channel for realtime events.
      const dc = pc.createDataChannel('oai-events');
      this.dc = dc;
      dc.addEventListener('message', (e) => this.handleServerEvent(e));

      // 5. SDP offer/answer handshake with OpenAI.
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (this.closed) { this.releaseResources(); return; }

      const sdpResponse = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!sdpResponse.ok) {
        throw new Error(`Realtime SDP exchange failed: ${sdpResponse.status}`);
      }

      const answerSdp = await sdpResponse.text();
      if (this.closed) { this.releaseResources(); return; }
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      this.scheduleGreetingFallback();
    } catch (error) {
      // Surface the real error instead of masking it as "Ended".
      this.releaseResources();
      this.onError(error);
      this.onStatus('error');
      throw error;
    }
  }

  handleServerEvent(messageEvent) {
    let event;
    try {
      event = JSON.parse(messageEvent.data);
    } catch {
      return;
    }
    this.onEvent(event);

    // Map realtime events to orb phases.
    switch (event.type) {
      case 'session.created': {
        this.clearGreetingFallbackTimer();
        this.sendVoiceGreeting();
        break;
      }
      case 'session.updated':
        // Ignore — greeting is sent once on session.created only.
        break;
      case 'error':
        // eslint-disable-next-line no-console
        console.warn('[voice] realtime error:', event);
        if (this.pendingGreetingResponse) {
          this.clearGreetingResponseTimer();
          this.pendingGreetingResponse = false;
          this.greetingAudioHeard = false;
          this.greetingResponseId = null;
          this.pendingGreetingCaption = null;
          this.clearGreetingPlaybackFallbackTimer();
          this.assistantTranscript = '';
          // Allow one retry if the greeting request was rejected (e.g. bad params).
          this.greetingSent = false;
        } else if (this.awaitingConnectGreeting) {
          this.finishConnectGreeting();
        } else if (this.phase === 'thinking') {
          this.setPhase('listening');
        }
        break;
      case 'input_audio_buffer.speech_started': {
        const startedMs = Date.now();
        // Started before the gate runs, so the bursts the gate discards — the false
        // triggers we are trying to characterise — are measured too.
        this.beginFeatureSegment(startedMs);
        if (this.canUserBargeIn()) {
          if (this.isNearFieldSpeech(startedMs)) {
            this.handleUserBargeIn();
          } else {
            this.scheduleBargeInRecheck();
          }
        } else if (this.shouldIgnoreVadTurn()) {
          break;
        }
        if (!this.activeUserTurn) {
          this.activeUserTurn = { bargeIn: this.userBargeInActive, awaitingTranscript: false };
        }
        this.micActivity.beginTurn(startedMs);
        break;
      }
      case 'input_audio_buffer.speech_stopped': {
        const ignoredByGate = this.shouldIgnoreVadTurn();
        const featureKey = this.finishFeatureSegment(Date.now(), ignoredByGate);
        if (ignoredByGate) break;
        if (!this.pendingGreetingResponse) {
          const stoppedMs = Date.now();
          const bargedIn = this.userBargeInActive;
          if (!this.activeUserTurn) {
            this.activeUserTurn = { bargeIn: bargedIn, awaitingTranscript: true };
          } else {
            this.activeUserTurn.awaitingTranscript = true;
            if (bargedIn) {
              this.activeUserTurn.bargeIn = true;
            }
          }
          const evidence = this.micActivity.finishTurn(stoppedMs);
          this.activeUserTurn.audio = this.micActivity.hasFreshSamples(stoppedMs) ? evidence : null;
          this.activeUserTurn.featureKey = featureKey;
          this.userBargeInActive = false;
          this.scheduleTranscriptValidationTimeout();
        }
        break;
      }
      case 'response.created':
        if (event.response?.id) {
          const responseId = event.response.id;
          if (this.pendingGreetingResponse && !this.greetingResponseId) {
            this.greetingResponseId = responseId;
          } else if (
            this.isConnectGreeting()
            && this.greetingResponseId
            && responseId !== this.greetingResponseId
          ) {
            this.sendEvent({ type: 'response.cancel' });
            break;
          } else if (this.shouldCancelStrayResponse()) {
            this.responseStatuses.set(responseId, 'cancelled');
            this.sendEvent({ type: 'response.cancel' });
            if (!this.pendingGreetingResponse && this.phase === 'thinking') {
              this.setPhase('listening');
            }
            break;
          }
          this.expectingClientResponse = false;
          this.activeResponseId = responseId;
          this.responseStatuses.set(responseId, 'in_progress');
        }
        if (!this.pendingGreetingResponse) {
          this.setPhase('thinking');
        }
        break;
      // GA + legacy audio output events
      case 'response.audio.delta':
      case 'response.output_audio.delta':
      case 'response.output_audio.done':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
          this.setPhase('greeting');
        } else {
          this.setPhase('speaking');
        }
        break;
      case 'output_audio_buffer.started':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
        } else if (this.pendingAssistantCommit) {
          this.pendingAssistantCommit.playbackStopped = false;
        }
        this.resumeAssistantPlayback();
        if (this.pendingGreetingResponse) {
          this.setPhase('greeting');
        } else {
          this.setPhase('speaking');
        }
        break;
      // GA + legacy assistant transcript events
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
        }
        if (typeof event.delta === 'string') {
          this.assistantTranscript += event.delta;
          if (this.pendingGreetingResponse && containsNonEnglishSpokenScript(this.assistantTranscript)) {
            this.retryEnglishGreeting();
            break;
          }
          this.onCaption({ role: 'assistant', text: this.assistantTranscript, final: false });
        }
        break;
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done': {
        const finalText = event.transcript || this.assistantTranscript;
        const responseId = event.response_id || event.response?.id || this.activeResponseId;
        if (this.pendingGreetingResponse) {
          if (finalText && containsNonEnglishSpokenScript(finalText)) {
            this.retryEnglishGreeting();
            break;
          }
          if (finalText) this.pendingGreetingCaption = finalText;
        } else if (finalText && responseId) {
          this.stageAssistantCaption(responseId, finalText);
        }
        this.assistantTranscript = '';
        break;
      }
      case 'conversation.item.input_audio_transcription.delta':
        if (this.shouldForwardUserCaption() && typeof event.delta === 'string' && event.delta) {
          this.onCaption({ role: 'user', text: event.delta, final: false });
        }
        break;
      case 'conversation.item.input_audio_transcription.completed': {
        const transcript = (event.transcript || '').trim();
        if (this.shouldForwardUserCaption() && transcript) {
          this.onCaption({ role: 'user', text: transcript, final: true });
        }
        if (this.activeUserTurn?.awaitingTranscript) {
          this.handleUserTranscriptCompleted(transcript);
        }
        break;
      }
      // Function-call events (GA emits these in addition to response.done)
      case 'response.function_call_arguments.done':
        this.runToolCall({
          name: event.name,
          call_id: event.call_id,
          arguments: event.arguments,
        });
        break;
      case 'response.output_item.done':
        if (event.item?.type === 'function_call') {
          this.runToolCall(event.item);
        }
        break;
      case 'response.done': {
        const response = event.response;
        const responseId = response?.id || this.activeResponseId;
        if (responseId) {
          const status = response?.status === 'completed' ? 'completed'
            : response?.status === 'cancelled' ? 'cancelled'
              : response?.status === 'failed' ? 'failed'
                : 'completed';
          this.responseStatuses.set(responseId, status);
          if (status === 'cancelled' || status === 'failed') {
            if (this.pendingAssistantCommit?.id === responseId) {
              this.pendingAssistantCommit = null;
            }
          }
        }
        this.activeResponseId = null;
        const hasToolCall = Array.isArray(response?.output)
          && response.output.some((item) => item?.type === 'function_call');
        if (this.pendingGreetingResponse) {
          if (hasToolCall) {
            this.pendingGreetingResponse = false;
            this.clearGreetingResponseTimer();
          } else if (response?.status === 'failed' || response?.status === 'cancelled') {
            this.recoverFromIncompleteGreeting();
          } else if (responseHasAudioOutput(response) || this.greetingAudioHeard) {
            this.greetingAudioHeard = true;
            this.clearGreetingResponseTimer();
            this.scheduleGreetingPlaybackFallback();
          }
        } else if (
          (response?.status === 'failed' || response?.status === 'cancelled')
          && this.phase === 'thinking'
        ) {
          this.setPhase('listening');
        } else if (responseId && response?.status === 'completed') {
          if (this.pendingAssistantCommit?.id === responseId) {
            this.pendingAssistantCommit.responseCompleted = true;
          }
          this.tryCommitAssistantCaption();
        }
        this.handleFunctionCalls(response);
        break;
      }
      case 'output_audio_buffer.stopped':
        if (this.pendingGreetingResponse) {
          this.pendingGreetingResponse = false;
          this.greetingAudioHeard = false;
          this.clearGreetingResponseTimer();
          this.clearGreetingPlaybackFallbackTimer();
          this.commitConnectGreetingCaption();
          this.finishConnectGreeting();
          this.beginAssistantEchoCooldown();
        } else {
          const skipCooldown = this.userBargeInActive;
          this.userBargeInActive = false;
          if (!skipCooldown) {
            this.beginAssistantEchoCooldown();
          }
          if (this.pendingAssistantCommit) {
            this.pendingAssistantCommit.playbackStopped = true;
          }
          this.tryCommitAssistantCaption();
          this.setPhase('listening');
        }
        break;
      default:
        break;
    }
  }

  // ─── Audio-level metering (reactive orb) ───────────────────────────────────
  ensureAudioContext() {
    if (!this.audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) this.audioCtx = new Ctx();
    }
    return this.audioCtx;
  }

  attachMicAnalyser(stream) {
    try {
      const ctx = this.ensureAudioContext();
      if (!ctx) return;
      const source = ctx.createMediaStreamSource(stream);
      this.micAnalyser = ctx.createAnalyser();
      // Sized for frequency resolution (~23Hz per bin) so the spectral bands land
      // accurately. Level reads take a short slice from the end of the same buffer.
      this.micAnalyser.fftSize = ANALYSER_FFT_SIZE;
      // Spectral tilt needs the current spectrum, not one smeared across ~5 frames.
      this.micAnalyser.smoothingTimeConstant = 0;
      source.connect(this.micAnalyser);
      this.micScratch = createFrameScratch(this.micAnalyser);
      this.micBands = createBandBins(ctx.sampleRate, this.micAnalyser.fftSize);
    } catch { /* metering is best-effort */ }
  }

  attachRemoteAnalyser(stream) {
    try {
      const ctx = this.ensureAudioContext();
      if (!ctx) return;
      const source = ctx.createMediaStreamSource(stream);
      this.remoteAnalyser = ctx.createAnalyser();
      // Short window: this only drives the orb, which should track playback tightly.
      this.remoteAnalyser.fftSize = 256;
      source.connect(this.remoteAnalyser);
      this.remoteScratch = createFrameScratch(this.remoteAnalyser);
    } catch { /* metering is best-effort */ }
  }

  static rms(analyser, scratch, sampleCount) {
    return readRms(analyser, scratch, sampleCount);
  }

  startLevelMetering() {
    const tick = () => {
      if (this.closed) return;
      const nowMs = Date.now();
      const frame = readFrameFeatures(this.micAnalyser, this.micScratch, this.micBands);
      // The gate keeps its original short window so its tuning still holds; the
      // feature segment gets the longer envelope window it needs for decay slope.
      const micLevel = frame ? frame.gateRms : 0;
      const remoteLevel = RealtimeVoiceClient.rms(this.remoteAnalyser, this.remoteScratch, GATE_SAMPLES);
      // Only the mic feeds the noise gate; the orb still reacts to both sides.
      this.micActivity.noteLevel(micLevel, nowMs);
      this.featureSegments.note(frame, nowMs);
      this.onLevel(Math.min(1, Math.max(micLevel, remoteLevel) * 2.2));
      this.levelRaf = window.requestAnimationFrame(tick);
    };
    this.levelRaf = window.requestAnimationFrame(tick);
  }

  // ─── Proximity feature capture (measurement only) ──────────────────────────

  /** Snapshots the gate's inputs at speech onset, before any decision changes them. */
  beginFeatureSegment(nowMs) {
    this.featureSegments.begin(nowMs, {
      phaseAtStart: this.phase,
      ignoredAtStart: this.shouldIgnoreVadTurn(),
      couldBargeIn: this.canUserBargeIn(),
      connectGreeting: this.isConnectGreeting(),
      echoCooldown: this.isInAssistantEchoCooldown(),
      nearFieldAtStart: this.isNearFieldSpeech(nowMs),
      noiseFloorAtStart: this.micActivity.noiseFloor,
      thresholdAtStart: this.micActivity.threshold(),
    });
  }

  /** Files the finished burst and returns its telemetry key, or null if unmeasured. */
  finishFeatureSegment(nowMs, ignoredByGate) {
    const segment = this.featureSegments.finish(nowMs);
    if (!segment?.features) return null;
    return this.telemetry.add({
      key: `${this.sessionKey}-${segment.id}`,
      session: this.sessionKey,
      outcome: ignoredByGate ? OUTCOMES.IGNORED_BY_GATE : OUTCOMES.PENDING,
      analysisSource: this.analysisStream?.source || 'processed',
      analysisProcessing: this.analysisStream?.processing || null,
      sampleRate: this.audioCtx?.sampleRate || null,
      bargeInFired: segment.bargeInFired,
      droppedFrames: segment.droppedFrames,
      ignoredAtStop: !!ignoredByGate,
      nearFieldAtStop: this.isNearFieldSpeech(nowMs),
      ...segment.context,
      ...segment.features,
    });
  }

  /** Opt into target-speaker isolation where the platform supports it. */
  async requestVoiceIsolation() {
    const track = this.micStream?.getAudioTracks?.()[0];
    if (!track?.applyConstraints) return;
    const supported = navigator.mediaDevices?.getSupportedConstraints?.() || {};
    if (!supported.voiceIsolation) return;
    try {
      await track.applyConstraints({ voiceIsolation: true });
    } catch { /* device refused it — the standard DSP constraints still apply */ }
  }

  // Sends a JSON event over the realtime data channel.
  sendEvent(event) {
    if (this.dc && this.dc.readyState === 'open') {
      this.dc.send(JSON.stringify(event));
    }
  }

  /** Refresh OpenAI session instructions with current Asia/Colombo date (like chat per-turn). */
  refreshSessionInstructions() {
    if (this.closed || !this.dc || this.dc.readyState !== 'open') {
      return Promise.resolve();
    }
    if (this.instructionsRefreshPromise) {
      return this.instructionsRefreshPromise;
    }
    this.instructionsRefreshPromise = refreshVoiceInstructions(this.userContext)
      .then(({ instructions }) => {
        if (this.closed || !instructions) return;
        this.sendEvent({
          type: 'session.update',
          session: { instructions },
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[voice] session instruction refresh failed:', error);
      })
      .finally(() => {
        this.instructionsRefreshPromise = null;
      });
    return this.instructionsRefreshPromise;
  }

  isInAssistantEchoCooldown() {
    return Date.now() < this.assistantEchoCooldownUntil;
  }

  beginAssistantEchoCooldown() {
    this.assistantEchoCooldownUntil = Date.now() + ASSISTANT_ECHO_COOLDOWN_MS;
    this.applyMicGate();
    if (this.echoCooldownTimer) {
      clearTimeout(this.echoCooldownTimer);
    }
    this.echoCooldownTimer = setTimeout(() => {
      this.echoCooldownTimer = null;
      this.applyMicGate();
    }, ASSISTANT_ECHO_COOLDOWN_MS);
  }

  shouldIgnoreVadTurn() {
    return this.isConnectGreeting()
      || this.phase === 'greeting'
      || this.phase === 'thinking'
      || this.phase === 'searching'
      || this.isInAssistantEchoCooldown();
  }

  canUserBargeIn() {
    return this.phase === 'speaking'
      && !this.isConnectGreeting()
      && !this.isInAssistantEchoCooldown();
  }

  shouldCancelStrayResponse() {
    if (this.expectingClientResponse) return false;
    if (this.pendingGreetingResponse) return false;
    return true;
  }

  shouldForwardUserCaption() {
    return !this.isConnectGreeting()
      && (this.phase === 'listening' || this.phase === 'speaking')
      && !this.isInAssistantEchoCooldown();
  }

  /** User finalized a VAD turn — respond only when transcript looks intentional. */
  async handleUserTranscriptCompleted(transcript) {
    this.clearTranscriptValidationTimeout();

    if (!this.activeUserTurn?.awaitingTranscript) return;

    const { bargeIn, audio, featureKey } = this.activeUserTurn;
    this.activeUserTurn = null;

    if (this.closed || this.isConnectGreeting()) {
      this.telemetry.annotate(featureKey, { outcome: OUTCOMES.ABANDONED, transcript });
      this.setPhase('listening');
      return;
    }

    const accepted = isMeaningfulUserSpeech(transcript, { bargeIn, audio });
    this.telemetry.annotate(featureKey, {
      outcome: accepted ? OUTCOMES.ACCEPTED : OUTCOMES.REJECTED,
      transcript,
      bargeIn,
      loudMs: audio?.loudMs ?? null,
      peakLevel: audio?.peakLevel ?? null,
      micNoiseFloor: audio?.noiseFloor ?? null,
      micThreshold: audio?.threshold ?? null,
    });

    if (!accepted) {
      // eslint-disable-next-line no-console
      console.log('[voice] ignored non-meaningful user turn:', JSON.stringify(transcript), audio);
      if (this.phase !== 'speaking') {
        this.setPhase('listening');
      }
      return;
    }

    // Late interruption: the speech was too quiet for the near-field gate, but the
    // transcript proves the user really did speak, so drop what is still playing.
    if (this.activeResponseId) {
      this.userBargeInActive = true;
      this.stopAssistantPlayback();
      this.cancelActiveResponse();
    }

    try {
      this.setPhase('thinking');
      await this.refreshSessionInstructions();
      if (this.closed) return;
      this.expectingClientResponse = true;
      this.sendEvent({ type: 'response.create' });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[voice] failed to start validated user response:', error);
      this.setPhase('listening');
    }
  }

  scheduleTranscriptValidationTimeout() {
    this.clearTranscriptValidationTimeout();
    this.transcriptWaitTimer = setTimeout(() => {
      this.transcriptWaitTimer = null;
      if (!this.activeUserTurn?.awaitingTranscript) return;
      // eslint-disable-next-line no-console
      console.log('[voice] user turn timed out waiting for transcript');
      this.telemetry.annotate(this.activeUserTurn.featureKey, {
        outcome: OUTCOMES.TRANSCRIPT_TIMEOUT,
      });
      this.activeUserTurn = null;
      if (this.phase !== 'speaking' && this.phase !== 'searching' && this.phase !== 'greeting') {
        this.setPhase('listening');
      }
    }, TRANSCRIPT_WAIT_MS);
  }

  clearTranscriptValidationTimeout() {
    if (this.transcriptWaitTimer) {
      clearTimeout(this.transcriptWaitTimer);
      this.transcriptWaitTimer = null;
    }
  }

  stageAssistantCaption(responseId, text) {
    const trimmed = (text || '').trim();
    if (!trimmed || !responseId) return;
    this.pendingAssistantCommit = {
      id: responseId,
      text: trimmed,
      responseCompleted: false,
      playbackStopped: true,
    };
  }

  tryCommitAssistantCaption() {
    const pending = this.pendingAssistantCommit;
    if (!pending || this.committedResponseIds.has(pending.id)) return;

    const status = this.responseStatuses.get(pending.id);
    if (status === 'cancelled' || status === 'failed') {
      this.pendingAssistantCommit = null;
      return;
    }
    if (!pending.responseCompleted) return;
    if (!pending.playbackStopped) return;

    const text = pending.text;
    if (text.length < MIN_ASSISTANT_CAPTION_CHARS) return;

    this.committedResponseIds.add(pending.id);
    this.pendingAssistantCommit = null;
    this.onCaption({ role: 'assistant', text, final: true });
  }

  /** One-time spoken hello — only after OpenAI signals the session is ready. */
  sendVoiceGreeting({ verbatimRetry = false } = {}) {
    if (this.greetingSent && !verbatimRetry) return;
    if (this.closed) return;
    if (!this.dc || this.dc.readyState !== 'open') return;
    if (!verbatimRetry) {
      this.greetingSent = true;
    }
    this.pendingGreetingResponse = true;
    this.greetingAudioHeard = false;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.assistantTranscript = '';
    this.setPhase('greeting');
    this.expectingClientResponse = true;
    this.sendEvent({
      type: 'response.create',
      response: {
        // GA Realtime: only ['audio'] or ['text'] — not both.
        output_modalities: ['audio'],
        instructions: verbatimRetry
          ? buildVerbatimEnglishGreetingInstructions(this.userContext)
          : buildVoiceGreetingInstructions(this.userContext),
      },
    });
    this.scheduleGreetingResponseTimeout();
  }

  /** Cancel a non-English greeting and retry once with a fixed English script. */
  retryEnglishGreeting() {
    if (this.closed || this.greetingRetryUsed) return;
    this.greetingRetryUsed = true;
    this.clearGreetingResponseTimer();
    this.clearGreetingPlaybackFallbackTimer();
    this.pendingGreetingCaption = null;
    this.assistantTranscript = '';
    this.greetingAudioHeard = false;
    this.greetingResponseId = null;
    this.stopAssistantPlayback();
    this.sendEvent({ type: 'response.cancel' });
    this.sendVoiceGreeting({ verbatimRetry: true });
  }

  isConnectGreeting() {
    return this.pendingGreetingResponse || this.awaitingConnectGreeting;
  }

  commitConnectGreetingCaption() {
    const text = (this.pendingGreetingCaption || this.assistantTranscript || '').trim();
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.assistantTranscript = '';
    if (text) {
      this.onCaption({ role: 'assistant', text, final: true });
    }
  }

  /** Connect greeting done — hand off to normal hands-free listening. */
  finishConnectGreeting() {
    this.awaitingConnectGreeting = false;
    this.greetingResponseId = null;
    this.setPhase('listening');
  }

  scheduleGreetingFallback() {
    this.clearGreetingFallbackTimer();
    this.greetingFallbackTimer = setTimeout(() => {
      this.greetingFallbackTimer = null;
      if (!this.closed && !this.greetingSent && this.dc?.readyState === 'open') {
        this.sendVoiceGreeting();
      }
    }, GREETING_FALLBACK_MS);
  }

  scheduleGreetingResponseTimeout() {
    this.clearGreetingResponseTimer();
    this.greetingResponseTimer = setTimeout(() => {
      this.greetingResponseTimer = null;
      if (!this.closed && this.pendingGreetingResponse && !this.greetingAudioHeard) {
        this.recoverFromIncompleteGreeting();
      }
    }, GREETING_RESPONSE_TIMEOUT_MS);
  }

  recoverFromIncompleteGreeting() {
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.assistantTranscript = '';
    this.clearGreetingResponseTimer();
    if (this.awaitingConnectGreeting) {
      this.finishConnectGreeting();
    } else if (this.phase === 'thinking' || this.phase === 'speaking') {
      this.setPhase('listening');
    }
  }

  clearGreetingFallbackTimer() {
    if (this.greetingFallbackTimer) {
      clearTimeout(this.greetingFallbackTimer);
      this.greetingFallbackTimer = null;
    }
  }

  clearGreetingResponseTimer() {
    if (this.greetingResponseTimer) {
      clearTimeout(this.greetingResponseTimer);
      this.greetingResponseTimer = null;
    }
  }

  scheduleGreetingPlaybackFallback() {
    this.clearGreetingPlaybackFallbackTimer();
    // iOS Safari may not emit output_audio_buffer.stopped — finish after playback window.
    this.greetingPlaybackFallbackTimer = setTimeout(() => {
      this.greetingPlaybackFallbackTimer = null;
      if (!this.closed && this.pendingGreetingResponse) {
        this.pendingGreetingResponse = false;
        this.clearGreetingResponseTimer();
        this.commitConnectGreetingCaption();
        this.finishConnectGreeting();
        this.beginAssistantEchoCooldown();
      }
    }, 8000);
  }

  clearGreetingPlaybackFallbackTimer() {
    if (this.greetingPlaybackFallbackTimer) {
      clearTimeout(this.greetingPlaybackFallbackTimer);
      this.greetingPlaybackFallbackTimer = null;
    }
  }

  clearGreetingTimers() {
    this.clearGreetingFallbackTimer();
    this.clearGreetingResponseTimer();
    this.clearGreetingPlaybackFallbackTimer();
  }

  // Detects function_call items in a completed response and runs them.
  handleFunctionCalls(response) {
    const output = response && Array.isArray(response.output) ? response.output : [];
    output
      .filter((item) => item && item.type === 'function_call')
      .forEach((item) => this.runToolCall(item));
  }

  async runToolCall(item) {
    const callId = item?.call_id;
    const toolName = item?.name;
    if (!callId || !toolName || this.handledCallIds.has(callId)) return;
    this.handledCallIds.add(callId);

    let args = {};
    try {
      args = item.arguments ? JSON.parse(item.arguments) : {};
    } catch {
      args = {};
    }

    // eslint-disable-next-line no-console
    console.log('[voice] function call:', toolName, args);

    let modelOutput = { error: 'Tool unavailable' };
    try {
      this.setPhase('searching');
      await this.refreshSessionInstructions();
      if (this.executeToolCall) {
        const result = await this.executeToolCall(toolName, args);
        if (this.closed) return;
        modelOutput = result?.modelOutput ?? {};
        // eslint-disable-next-line no-console
        console.log('[voice] tool exec result:', toolName, {
          spaces: result?.spaces?.length ?? 0,
          found: result?.modelOutput?.found,
        });
        this.onToolResult(result || {}, { name: toolName, args });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[voice] tool exec failed:', toolName, error);
      modelOutput = { error: error?.message || 'Tool execution failed' };
    }

    // Return the tool result to the model, then ask it to respond.
    this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(modelOutput),
      },
    });
    this.expectingClientResponse = true;
    this.sendEvent({ type: 'response.create' });
    this.setPhase('thinking');
  }

  /**
   * True when mic loudness clearly rises above the ambient noise floor.
   *
   * Deliberately false when metering is unavailable: an unverified interrupt would
   * discard an answer the user is still listening to. Genuine speech that misses
   * this gate still interrupts a moment later, once its transcript is validated.
   */
  isNearFieldSpeech(nowMs = Date.now()) {
    if (!this.micActivity.hasFreshSamples(nowMs)) return false;
    return this.micActivity.isNearFieldConfirmed(nowMs);
  }

  /** Speech can start below the noise floor — look again shortly before giving up. */
  scheduleBargeInRecheck() {
    this.clearBargeInRecheck();
    this.bargeInRecheckTimer = setTimeout(() => {
      this.bargeInRecheckTimer = null;
      if (this.closed || !this.canUserBargeIn()) return;
      if (this.isNearFieldSpeech(Date.now())) this.handleUserBargeIn();
    }, BARGE_IN_RECHECK_MS);
  }

  clearBargeInRecheck() {
    if (this.bargeInRecheckTimer) {
      clearTimeout(this.bargeInRecheckTimer);
      this.bargeInRecheckTimer = null;
    }
  }

  /** User spoke over Spacey — stop playback, cancel the response, start a new turn. */
  handleUserBargeIn() {
    if (this.isConnectGreeting() || this.isInAssistantEchoCooldown()) return;
    this.featureSegments.markBargeIn();
    this.clearBargeInRecheck();
    this.userBargeInActive = true;
    this.stopAssistantPlayback();
    this.cancelActiveResponse();
    this.setPhase('listening');
  }

  /** Drops the in-flight response, if any. Safe to call when none is active. */
  cancelActiveResponse() {
    const responseId = this.activeResponseId;
    if (!responseId) return false;
    this.responseStatuses.set(responseId, 'cancelled');
    if (this.pendingAssistantCommit?.id === responseId) {
      this.pendingAssistantCommit = null;
    }
    this.sendEvent({ type: 'response.cancel' });
    this.assistantTranscript = '';
    this.activeResponseId = null;
    return true;
  }

  stopAssistantPlayback() {
    if (this.audioEl) {
      try {
        this.audioEl.pause();
      } catch { /* noop */ }
    }
    this.sendEvent({ type: 'output_audio_buffer.clear' });
  }

  resumeAssistantPlayback() {
    if (this.audioEl) {
      try {
        const playPromise = this.audioEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch { /* noop */ }
    }
  }

  setPhase(phase) {
    if (this.closed) return;
    this.phase = phase;
    this.onPhase(phase);
    this.applyMicGate();
  }

  /** Mic open while listening or during normal speech (barge-in). Off for greeting/thinking/search/cooldown. */
  applyMicGate() {
    const micOpen = !this.userMuted
      && (this.phase === 'listening' || this.phase === 'speaking')
      && this.phase !== 'greeting'
      && !this.isConnectGreeting()
      && !this.isInAssistantEchoCooldown();
    this.setMicTracksEnabled(micOpen);
  }

  setMicTracksEnabled(enabled) {
    if (!this.micStream) return;
    this.micStream.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  setMuted(muted) {
    this.userMuted = muted;
    this.applyMicGate();
  }

  // Releases media/network resources without emitting a status change.
  releaseResources() {
    this.closed = true;
    this.clearGreetingTimers();
    this.clearTranscriptValidationTimeout();
    this.activeUserTurn = null;
    this.clearBargeInRecheck();
    this.micActivity.reset();
    this.featureSegments.reset();
    this.telemetry.finalisePending();
    if (this.echoCooldownTimer) {
      clearTimeout(this.echoCooldownTimer);
      this.echoCooldownTimer = null;
    }
    this.pendingGreetingResponse = false;
    this.userBargeInActive = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = false;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    if (this.levelRaf) {
      try { window.cancelAnimationFrame(this.levelRaf); } catch { /* noop */ }
      this.levelRaf = null;
    }
    try {
      if (this.audioCtx) this.audioCtx.close();
    } catch { /* noop */ }
    this.audioCtx = null;
    this.micAnalyser = null;
    this.remoteAnalyser = null;
    this.micScratch = null;
    this.micBands = null;
    this.remoteScratch = null;
    try {
      if (this.dc) this.dc.close();
    } catch { /* noop */ }
    closeAnalysisStream(this.analysisStream);
    this.analysisStream = null;
    try {
      if (this.micStream) this.micStream.getTracks().forEach((t) => t.stop());
    } catch { /* noop */ }
    try {
      if (this.pc) this.pc.close();
    } catch { /* noop */ }
    if (this.audioEl) {
      this.audioEl.srcObject = null;
      this.audioEl = null;
    }
    this.dc = null;
    this.pc = null;
    this.micStream = null;
  }

  // User-initiated end of the session.
  stop() {
    this.releaseResources();
    this.onStatus('closed');
  }
}
